/**
 * DEFUSE S15 — per-kind descriptor registry assembly (barrel).
 *
 * Each entity kind's descriptor list = `buildCommonDescriptors(kind)` (the
 * shared object props present in that kind's roster) ++ its special geometry /
 * type-specific table. Wipeout has no special table (common object props only).
 */

import type { EntityType } from "@dxf-ai-editor/contracts";
import { entityTypeSchema } from "@dxf-ai-editor/contracts";

import { buildCommonDescriptors } from "./common";
import { arcDescriptors, circleDescriptors, lineDescriptors, pointDescriptors } from "./simpleGeometry";
import { ellipseDescriptors, lwpolylineDescriptors, splineDescriptors } from "./curves";
import { mtextDescriptors, textDescriptors } from "./text";
import {
  dimensionDescriptors,
  multileaderDescriptors,
  revisionCloudDescriptors,
} from "./annotation";
import {
  arrayRectDescriptors,
  blockDescriptors,
  hatchDescriptors,
  viewportDescriptors,
  wipeoutDescriptors,
} from "./containers";
import {
  oleFrameDescriptors,
  polylineDescriptors,
  tableDescriptors,
} from "./legacyPolylineTableOle";
import type { TypedPropertyDescriptor } from "./types";

/** special (non-common) descriptor table per kind. Kinds absent here surface
 *  common object properties only (Wipeout — grip/frame-editing via commands). */
const SPECIAL_DESCRIPTORS: Partial<Record<EntityType, readonly TypedPropertyDescriptor[]>> = {
  Line: lineDescriptors,
  Point: pointDescriptors,
  Circle: circleDescriptors,
  Arc: arcDescriptors,
  Ellipse: ellipseDescriptors,
  Spline: splineDescriptors,
  LWPOLYLINE: lwpolylineDescriptors,
  Text: textDescriptors,
  MText: mtextDescriptors,
  Dimension: dimensionDescriptors,
  Multileader: multileaderDescriptors,
  "Revision Cloud": revisionCloudDescriptors,
  Hatch: hatchDescriptors,
  Block: blockDescriptors,
  "Rectangular Associative Array": arrayRectDescriptors,
  Viewport: viewportDescriptors,
  Wipeout: wipeoutDescriptors,
  Polyline: polylineDescriptors,
  Table: tableDescriptors,
  "OLE Frame": oleFrameDescriptors,
};

/** the assembled foundation — every editable/shown property, per kind, typed. */
export const TYPED_PROPERTY_DESCRIPTOR_REGISTRY: Record<
  EntityType,
  readonly TypedPropertyDescriptor[]
> = (() => {
  const registry = {} as Record<EntityType, readonly TypedPropertyDescriptor[]>;
  for (const kind of entityTypeSchema.options) {
    registry[kind] = [...buildCommonDescriptors(kind), ...(SPECIAL_DESCRIPTORS[kind] ?? [])];
  }
  return registry;
})();
